import FilterChip from 'LuxKit/Chips/FilterChip'
import Group from 'components/utils/Group'
import React, { useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import { FlightDealsGridFilterItem } from './FlightsDealsGridFilterItem'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useToggle from 'hooks/useToggle'
import { ALL_KEY, FilterType } from './FlightsDealsGrid'
import { fareCabinLabelMap, FlightsClassTypes } from 'constants/flight'

const FiltersContainer = styled(Group)`
  padding: 0 ${rem(16)};
`

interface Filter {
  label: string
  value: string
  price?: number
}

interface Props {
  airlines?: Array<Filter>
  cabins: Array<Filter>
  selectedAirlines?: Set<string>
  selectedCabins: Set<string>
  handleToggle: (type: FilterType, value: string) => void
}

export function FlightDealsGridStaticFilterPills(props: Props) {
  const { airlines, cabins, selectedAirlines, selectedCabins, handleToggle } = props
  const airlinesDropdownTrigger = useRef<HTMLButtonElement>(null)
  const cabinsDropdownTrigger = useRef<HTMLButtonElement>(null)
  const { value: showAirlinesFilter, toggle: toggleAirlinesFilter } = useToggle()
  const { value: showCabinsFilter, toggle: toggleCabinsFilter } = useToggle()

  const showAirlinesNum = (selectedAirlines?.size || 0) > 0 && !selectedAirlines?.has(ALL_KEY)
  const showCabinsNum = selectedCabins.size > 0 && !selectedCabins.has(ALL_KEY)

  return (
    <>
      {airlines && (
        <FilterChip
          size="medium"
          ref={airlinesDropdownTrigger}
          onClick={toggleAirlinesFilter}
          endIcon={showAirlinesFilter ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        >
          {!showAirlinesNum ? 'All airlines' : 'Airlines'} {showAirlinesNum && `(${selectedAirlines?.size})`}
        </FilterChip>
      )}

      <FilterChip
        size="medium"
        ref={cabinsDropdownTrigger}
        endIcon={showAirlinesFilter ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        onClick={toggleCabinsFilter}
      >
        {!showCabinsNum ? 'All cabins' : 'Cabins'} {showCabinsNum && `(${selectedCabins.size})`}
      </FilterChip>

      {airlines && (
        <DropdownList
          size="M"
          open={showAirlinesFilter}
          triggerRef={airlinesDropdownTrigger}
          anchorRef={airlinesDropdownTrigger}
          onClose={toggleAirlinesFilter}
          placement="bottom-start"
        >
          <FiltersContainer direction="vertical">
            <VerticalSpacer gap={16}>
              <FlightDealsGridFilterItem
                checked={selectedAirlines?.has(ALL_KEY) || false}
                onCheckedChange={() => handleToggle(FilterType.AIRLINES, ALL_KEY)}
                label="All airlines"
              />

              {airlines.map(airline => (
                <FlightDealsGridFilterItem
                  key={airline.value}
                  checked={selectedAirlines?.has(airline.value) || false}
                  onCheckedChange={() => handleToggle(FilterType.AIRLINES, airline.value)}
                  label={airline.label}
                  price={airline.price}
                />
              ))}
            </VerticalSpacer>
          </FiltersContainer>
        </DropdownList>
      )}

      <DropdownList
        size="M"
        open={showCabinsFilter}
        triggerRef={cabinsDropdownTrigger}
        anchorRef={cabinsDropdownTrigger}
        onClose={toggleCabinsFilter}
        placement="bottom-start"
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsGridFilterItem
              checked={selectedCabins.has(ALL_KEY)}
              onCheckedChange={() => handleToggle(FilterType.CABINS, ALL_KEY)}
              label="All cabins"
            />

            {cabins.map(cabin => (
              <FlightDealsGridFilterItem
                key={cabin.value}
                checked={selectedCabins.has(cabin.value)}
                onCheckedChange={() => handleToggle(FilterType.CABINS, cabin.value)}
                label={fareCabinLabelMap[cabin.value as FlightsClassTypes]}
                price={cabin.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>
    </>
  )
}
